import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type AddonConfig = {
  id: string;
  name: string;
  price: number;
  description: string;
  isActive: boolean;
  minTier?: string;
};

type AddonConfigContextType = {
  addonConfigMap: Record<string, AddonConfig>;
  loading: boolean;
  refresh: () => Promise<void>;
};

const AddonConfigContext = createContext<AddonConfigContextType | undefined>(undefined);

const DEFAULT_ADDONS: Record<string, AddonConfig> = {
  OPTIONS_ANALYTICS_ADDON: {
    id: 'OPTIONS_ANALYTICS_ADDON',
    name: '스마트머니 옵션/공매도 분석 부가상품',
    price: 24900,
    description: '스마트머니 추적을 위한 옵션 체인, Greeks 파라미터, 공매도 비율 및 기관 포지션 심층 분석을 제공합니다.',
    isActive: true,
    minTier: 'TIER4',
  },
  AI_PERSONALIZATION_ADDON: {
    id: 'AI_PERSONALIZATION_ADDON',
    name: 'AI 개인화 & 모델 설정 부가상품',
    price: 9900,
    description: '분석 단계별 AI 모델, 프롬프트, API 키를 사용자 정의하여 나만의 분석 엔진을 구축합니다.',
    isActive: true,
    minTier: 'TIER5',
  },
  CHAT_PREDICTION_ADDON: {
    id: 'CHAT_PREDICTION_ADDON',
    name: 'AI 챗 플러스 퀀트 분석기 (AI Chat Plus)',
    price: 12900,
    description: '매크로 지표, 실시간 시장 데이터 질의 및 포트폴리오 기술 지표 산출을 제공하는 대화형 데이터 분석 소프트웨어입니다.',
    isActive: true,
    minTier: 'TIER4',
  },
  API_ACCESS_ADDON: {
    id: 'API_ACCESS_ADDON',
    name: 'Developer JSON Raw Data API 부가상품',
    price: 19900,
    description: '나만의 퀀트 전략을 개발하기 위해, 서버가 수집하고 요약한 정제 Macro 지표 데이터셋을 JSON API 형식으로 직접 호출해 쓸 수 있게 합니다.',
    isActive: true,
    minTier: 'TIER5',
  },
  addon_agent_standard: {
    id: 'addon_agent_standard',
    name: '시그널 레이더 Standard (Signal Radar Standard)',
    price: 29000,
    description: '24/7 사용자 정의 기술적 지표 조건 도달 시 텔레그램/웹 알림 전송 및 모의 시뮬레이션 지원 (월 500회 알림)',
    isActive: true,
    minTier: 'TIER3',
  },
  addon_agent_pro: {
    id: 'addon_agent_pro',
    name: '시그널 레이더 Pro (Signal Radar Pro)',
    price: 89000,
    description: '실시간 퀀트 데이터 피드, VIX 변동성 지표 기반 리스크 시뮬레이터 및 멀티채널 웹훅 알림 (월 3,000회 알림)',
    isActive: true,
    minTier: 'TIER4',
  },
  addon_agent_vip: {
    id: 'addon_agent_vip',
    name: '시그널 레이더 VIP (Signal Radar VIP)',
    price: 299000,
    description: '전용 Worker 파이프라인 기반 초고속 지표 연산 및 무제한 웹훅 시그널 알림 소프트웨어 (Unlimited)',
    isActive: true,
    minTier: 'TIER5',
  },
  addon_smart_order_standard: {
    id: 'addon_smart_order_standard',
    name: '스마트 주문 Standard (Smart Order Standard)',
    price: 29000,
    description: '실계좌 연동 스마트 주문 Standard 부가상품입니다. 실시간 시그널 웹훅 알림과 HITL 1-Click 주문 실행을 지원합니다. (월 500회 알림)',
    isActive: true,
    minTier: 'TIER3',
  },
  addon_smart_order_pro: {
    id: 'addon_smart_order_pro',
    name: '스마트 주문 Pro (Smart Order Pro)',
    price: 89000,
    description: '실계좌 연동 스마트 주문 실행 부가상품입니다. 주문 라우팅, 리스크 가드레일, HITL/Full-Auto 집행을 지원합니다. (월 3,000회 알림)',
    isActive: true,
    minTier: 'TIER4',
  },
  addon_smart_order_vip: {
    id: 'addon_smart_order_vip',
    name: '스마트 주문 VIP (Smart Order VIP)',
    price: 299000,
    description: '최우선 실행 라우팅과 확장된 리스크 한도, 무제한 시그널 연동을 제공하는 실계좌 전용 스마트 주문 VIP 부가상품입니다. (Unlimited)',
    isActive: true,
    minTier: 'TIER5',
  },
};

export function AddonConfigProvider({ children }: { children: ReactNode }) {
  const [addonConfigMap, setAddonConfigMap] = useState<Record<string, AddonConfig>>(DEFAULT_ADDONS);
  const [loading, setLoading] = useState(true);

  const fetchAddons = async () => {
    try {
      const res = await fetch('/api/addon-config');
      if (!res.ok) {
        console.warn(`[AddonConfigContext] /api/addon-config returned ${res.status}, using default configs`);
        setLoading(false);
        return;
      }
      const data: any = await res.json();
      const list = Array.isArray(data) ? data : data?.addons || [];
      if (list.length > 0) {
        setAddonConfigMap((prev) => {
          const next = { ...prev };
          list.forEach((item: any) => {
            if (item.id) {
              const active = item.isActive !== undefined ? Boolean(item.isActive) : (item.is_active === 1 || item.is_active === '1' || item.is_active === true);
              next[item.id] = {
                id: item.id,
                name: item.name || prev[item.id]?.name || item.id,
                price: Number(item.price !== undefined ? item.price : prev[item.id]?.price || 0),
                description: item.description || prev[item.id]?.description || '',
                isActive: active,
                minTier: item.minTier || item.min_tier || prev[item.id]?.minTier || 'FREE',
              };
            }
          });
          return next;
        });
      }
    } catch (e) {
      console.warn('Failed to load addon config, using defaults:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddons();
  }, []);

  return (
    <AddonConfigContext.Provider value={{ addonConfigMap, loading, refresh: fetchAddons }}>
      {children}
    </AddonConfigContext.Provider>
  );
}

export function useAddonConfig() {
  const context = useContext(AddonConfigContext);
  if (context === undefined) {
    throw new Error('useAddonConfig must be used within an AddonConfigProvider');
  }
  return context;
}
